// Tool層の型定義。
//
// 【設計の要】ツールの実行は「ブラウザ側」で行う。
// LLMサーバ(Gateway/Ollama)は業務データに一切触れず、tool_call の指示を返すだけ。
// 実際のFirestore読取は、ログイン中ユーザー自身の権限で行われるため、
// Firestoreセキュリティルールと RBAC（憲法第7条）がそのまま効く。
import type { z } from 'zod';

/** Ollama(OpenAI互換) に渡す function 定義。 */
export interface ToolDefinition {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters: {
      type: 'object';
      properties: Record<string, unknown>;
      required?: string[];
    };
  };
}

/** LLM が返す tool_call。 */
export interface ToolCall {
  id?: string;
  function: {
    name: string;
    /** Ollama はオブジェクトで返すが、文字列で返す実装もあるため両対応する。 */
    arguments: Record<string, unknown> | string;
  };
}

/** 書込ツールが返す成功結果。チャットの結果カードはこの値から「システム側が」描画する。 */
export interface WriteToolSuccess {
  kind: 'create' | 'update';
  resource: string;
  /** 例 '見積' */
  label: string;
  code: string;
  id: string;
  /** レコードを開く画面のパス */
  url: string;
}

/** 書込ツールの結果から成功情報を取り出す（結果カード描画用の判定）。 */
export function asWriteSuccess(result: unknown): WriteToolSuccess | null {
  if (typeof result !== 'object' || result === null) return null;
  const r = result as Record<string, unknown>;
  if (r.kind !== 'create' && r.kind !== 'update') return null;
  if (typeof r.code !== 'string' || typeof r.id !== 'string' || typeof r.url !== 'string') return null;
  return r as unknown as WriteToolSuccess;
}

/** ツール実行時のコンテキスト。書込ツールが「誰の権限で」実行するかに使う。 */
export interface ToolContext {
  /** ログイン中ユーザーのID。未ログインなら null（書込ツールはエラーを返す）。 */
  userId: string | null;
}

/**
 * 登録するツール1件。
 * schema で引数を検証し、execute で既存の service 関数を呼ぶ。
 */
export interface SecretaryTool<TArgs = unknown> {
  definition: ToolDefinition;
  /** LLM が渡してくる引数の検証。不正なら例外（呼び出し側が日本語エラーに変換）。 */
  schema: z.ZodType<TArgs>;
  /** 実行本体。戻り値は「LLMに渡す圧縮済みの結果」。読取ツールは ctx を無視してよい。 */
  execute: (args: TArgs, ctx: ToolContext) => Promise<unknown>;
}
